/*
 * <testimonials-carousel>
 * Drives the bottom-layout embla <carousel-component> (touch + mouse drag is
 * handled natively by embla via data-draggable). This element wires the pill
 * prev/next buttons and the progress bar to embla's API.
 *
 * The progress fill follows the scroll in real time, but writes are coalesced
 * to one per animation frame (rAF) and rounded to whole percents, and skipped
 * when unchanged — so embla's per-frame scroll events don't spam inline-style
 * mutations (which otherwise lag hard with DevTools open).
 */
class TestimonialsCarousel extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector('carousel-component');
    this.prevBtn = this.querySelector('[data-direction="prev"]');
    this.nextBtn = this.querySelector('[data-direction="next"]');
    if (!this.carousel) return;

    this.lastProgress = -1;
    this.rafId = 0;
    this.requestUpdate = this.requestUpdate.bind(this);
    this.render = this.render.bind(this);
    this.onPrev = () => this.embla && this.embla.scrollPrev();
    this.onNext = () => this.embla && this.embla.scrollNext();
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);

    this.waitForEmbla();
  }

  // embla initialises asynchronously after window events; poll for it.
  waitForEmbla(attempt = 0) {
    const embla = this.carousel && this.carousel.embla;
    if (embla) {
      this.embla = embla;
      embla.on('scroll', this.requestUpdate);
      embla.on('select', this.requestUpdate);
      embla.on('settle', this.requestUpdate);
      embla.on('reInit', this.requestUpdate);
      this.render();
      return;
    }
    if (attempt < 60) setTimeout(() => this.waitForEmbla(attempt + 1), 50);
  }

  // Coalesce bursts of scroll events into a single write per frame.
  requestUpdate() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      this.render();
    });
  }

  render() {
    if (!this.embla) return;
    let p = this.embla.scrollProgress();
    if (Number.isNaN(p)) p = 0;
    p = Math.round(Math.max(0, Math.min(1, p)) * 100);
    if (p !== this.lastProgress) {
      this.lastProgress = p;
      this.style.setProperty('--gsc-slider-grid-progress', p + '%');
    }
    if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', !this.embla.canScrollPrev());
    if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', !this.embla.canScrollNext());
  }

  disconnectedCallback() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
    if (this.embla) {
      this.embla.off('scroll', this.requestUpdate);
      this.embla.off('select', this.requestUpdate);
      this.embla.off('settle', this.requestUpdate);
      this.embla.off('reInit', this.requestUpdate);
    }
  }
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
